import React from "react";
import { createRoot } from "react-dom/client";
import KnowledgeMapEmbed from "./KnowledgeMapEmbed";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const STYLE_MARKER = "data-knowmapped-embed-v1-style";
const EMBED_FONT_BASE = new URL(/* @vite-ignore */ "./fonts", import.meta.url).href;
let instanceCounter = 0;
const legacyRoots = new WeakMap();
const ElementBase = typeof HTMLElement === "undefined" ? class {} : HTMLElement;

function isValidMapId(value) {
  return UUID_PATTERN.test(String(value || ""));
}

function booleanAttribute(element, name, defaultValue = false) {
  const value = element.getAttribute(name);
  if (value === null) return defaultValue;
  return value !== "false";
}

function validCssHeight(value) {
  const candidate = String(value || "").trim();
  if (!candidate) return null;
  if (typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("height", candidate)) {
    return candidate;
  }
  // CSSStyleDeclaration performs the final parsing/sanitization. This fallback
  // keeps the element usable in older browsers without accepting declarations.
  if (/^(?:\d+(?:\.\d+)?(?:px|rem|em|vh|vw|vmin|vmax|%|ch)|(?:calc|min|max|clamp)\([^;{}]+\))$/i.test(candidate)) {
    return candidate;
  }
  return null;
}

function validWidthMode(value) {
  const candidate = String(value || "").trim().toLowerCase();
  return ["normal", "wide", "full"].includes(candidate) ? candidate : "normal";
}

function elementWidthMode(element) {
  const explicitWidth = String(element.getAttribute("data-width") || "").trim().toLowerCase();
  if (["normal", "wide", "full"].includes(explicitWidth)) return explicitWidth;

  return ["normal", "wide", "full"].find((width) =>
    element.classList.contains(`knowledge-map-embed--${width}`)
  ) || "normal";
}

function ensureStylesheet() {
  if (typeof document === "undefined") return;
  if (document.querySelector(`link[${STYLE_MARKER}]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  // The stylesheet is intentionally deployed beside the versioned embed
  // module, rather than bundled as a JavaScript asset URL.
  link.href = new URL(/* @vite-ignore */ "./knowmapped-embed.css", import.meta.url).href;
  link.setAttribute(STYLE_MARKER, "true");
  document.head.appendChild(link);
}

function InvalidMap({ message }) {
  return <p className="knowledge-map-embed__error" role="alert">{message}</p>;
}

export class KnowmappedMapElement extends ElementBase {
  static observedAttributes = [
    "map-id",
    "show-title",
    "height",
    "data-show-frame",
    "data-show-background",
    "data-exploration",
  ];

  constructor() {
    super();
    this.root = null;
    this.instanceId = "";
  }

  connectedCallback() {
    ensureStylesheet();
    if (!this.root) {
      instanceCounter += 1;
      this.instanceId = `knowmapped-map-${instanceCounter}`;
      this.root = createRoot(this);
    }
    this.renderMap();
  }

  disconnectedCallback() {
    this.root?.unmount();
    this.root = null;
  }

  attributeChangedCallback() {
    if (this.isConnected && this.root) this.renderMap();
  }

  renderMap() {
    const mapId = String(this.getAttribute("map-id") || "").trim();
    const height = validCssHeight(this.getAttribute("height"));
    const showTitle = booleanAttribute(this, "show-title", true);
    const showFrame = booleanAttribute(this, "data-show-frame", true);
    const showBackground = booleanAttribute(this, "data-show-background", true);
    const explorationEnabled = booleanAttribute(this, "data-exploration", true);
    const widthMode = elementWidthMode(this);

    this.classList.remove("knowledge-map-embed--normal", "knowledge-map-embed--wide", "knowledge-map-embed--full", "knowledge-map-embed--frameless");
    this.classList.add("knowledge-map-embed", `knowledge-map-embed--${widthMode}`);
    if (!showFrame) this.classList.add("knowledge-map-embed--frameless");
    this.dataset.mapId = mapId;
    this.dataset.showTitle = String(showTitle);
    this.dataset.width = widthMode;
    this.dataset.heightMode = height ? "fixed" : "auto";

    if (height) this.style.height = height;
    else this.style.removeProperty("height");

    if (!isValidMapId(mapId)) {
      this.root.render(<InvalidMap message="This public knowledge map could not be loaded." />);
      return;
    }

    this.root.render(
      <KnowledgeMapEmbed
        instanceId={this.instanceId}
        mapId={mapId}
        apiBase={this.getAttribute("data-api-base") || "https://app.knowmapped.com"}
        publicMapBase={this.getAttribute("data-public-map-base") || "https://knowmapped.com/public-data"}
        publicViewerBase={this.getAttribute("data-public-viewer-base") || "https://knowmapped.com"}
        fontBaseUrl={EMBED_FONT_BASE}
        showTitle={showTitle}
        showFrame={showFrame}
        showBackground={showBackground}
        widthMode={widthMode}
        heightMode={height ? "fixed" : "auto"}
        explorationEnabled={explorationEnabled}
        loadDefaultFont
      />
    );
  }
}

function mountLegacyElement(element) {
  if (legacyRoots.has(element)) return;
  ensureStylesheet();
  const mapId = String(element.dataset.mapId || "").trim();
  if (!isValidMapId(mapId)) {
    const error = document.createElement("p");
    error.className = "knowledge-map-embed__error";
    error.setAttribute("role", "alert");
    error.textContent = "This public knowledge map could not be loaded.";
    element.replaceChildren(error);
    return;
  }

  const widthMode = elementWidthMode(element);
  element.classList.remove("knowledge-map-embed--normal", "knowledge-map-embed--wide", "knowledge-map-embed--full");
  element.classList.add("knowledge-map-embed", `knowledge-map-embed--${widthMode}`);
  element.dataset.width = widthMode;
  instanceCounter += 1;
  const root = createRoot(element);
  legacyRoots.set(element, root);
  root.render(
    <KnowledgeMapEmbed
      instanceId={`knowmapped-legacy-${instanceCounter}`}
      mapId={mapId}
      apiBase={element.dataset.apiBase || "https://app.knowmapped.com"}
      publicMapBase={element.dataset.publicMapBase || "https://knowmapped.com/public-data"}
      publicViewerBase={element.dataset.publicViewerBase || "https://knowmapped.com"}
      fontBaseUrl={EMBED_FONT_BASE}
      showTitle={element.dataset.showTitle !== "false"}
      showFrame={element.dataset.showFrame !== "false"}
      showBackground={element.dataset.showBackground !== "false"}
      widthMode={widthMode}
      heightMode={element.dataset.heightMode || "auto"}
      explorationEnabled={element.dataset.exploration !== "false"}
      loadDefaultFont
    />
  );
}

function initializeLegacyEmbeds(root = document) {
  root
    .querySelectorAll?.('[data-knowledge-map-embed="true"]:not(knowmapped-map)')
    .forEach(mountLegacyElement);
}

function observeLegacyEmbeds() {
  if (!document.body || !window.MutationObserver) return;
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.('[data-knowledge-map-embed="true"]:not(knowmapped-map)')) {
          mountLegacyElement(node);
        }
        initializeLegacyEmbeds(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}

if (typeof customElements !== "undefined" && !customElements.get("knowmapped-map")) {
  customElements.define("knowmapped-map", KnowmappedMapElement);
}

if (typeof document !== "undefined") {
  const initialize = () => {
    initializeLegacyEmbeds();
    observeLegacyEmbeds();
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }
}

export { UUID_PATTERN, isValidMapId, validCssHeight, validWidthMode, ensureStylesheet, mountLegacyElement, initializeLegacyEmbeds };
